import { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ServiceProviderCard from '../job-board/components/ServiceProviderCard';
import FilterPanel from '../job-board/components/FilterPanel';
import SearchBar from '../job-board/components/SearchBar';
import TagFilter from '../job-board/components/TagFilter';
import { workerProfilesService } from '../../services/supabaseService';
import Select from '../../components/ui/Select';


const ServiceProviders = () => {
  const [viewMode, setViewMode] = useState('card');
  const [sortBy, setSortBy] = useState('rating');
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [savedProviders, setSavedProviders] = useState([]);
  const [compareList, setCompareList] = useState([]);
  const [showComparison, setShowComparison] = useState(false);

  const [filters, setFilters] = useState({
    category: 'all',
    location: 'all',
    budget: { min: '', max: '' },
    availability: 'all',
    verificationLevel: 'all',
    minRating: null
  });

  const availableTags = [
    'Plumbing', 'Electrical', 'Carpentry', 'Cleaning', 'Babysitting',
    'Tutoring', 'Driving', 'Cooking', 'Laundry', 'Errands',
    'Tech Support', 'Beauty Services', 'Painting', 'Gardening',
    'Pet Care', 'Moving', 'Repair', 'Installation', 'Maintenance'
  ];

  useEffect(() => {
    fetchWorkers();
  }, [filters, selectedTags]);

  useEffect(() => {
    if (searchQuery?.length > 0) {
      const suggestions = generateSearchSuggestions(searchQuery);
      setSearchSuggestions(suggestions);
    } else {
      setSearchSuggestions([]);
    }
  }, [searchQuery]);

  const generateSearchSuggestions = (query) => {
    const lowerQuery = query?.toLowerCase();
    const allSuggestions = [
      ...availableTags,
      'Licensed plumber',
      'Certified electrician',
      'Experienced carpenter',
      'Professional cleaner',
      'Certified babysitter',
      'Math tutor',
      'Driver with car',
      'Professional chef',
      'Laundry specialist'
    ];

    return allSuggestions
      ?.filter((s) => s?.toLowerCase()?.includes(lowerQuery))
      ?.slice(0, 6);
  };

  const fetchWorkers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await workerProfilesService?.getWorkers({
        category: filters?.category,
        location: filters?.location,
        minRate: filters?.budget?.min,
        maxRate: filters?.budget?.max,
        availability: filters?.availability,
        verificationLevel: filters?.verificationLevel,
        minRating: filters?.minRating
      });
      setWorkers(data || []);
    } catch (err) {
      console.error('Error fetching workers:', err);
      setError('Failed to load service providers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    fetchWorkers();
  };

  const handleTagToggle = (tag) => {
    setSelectedTags((prev) =>
      prev?.includes(tag) ? prev?.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleViewProfile = (provider) => {
    window.location.href = `/worker-profile?id=${provider?.id}`;
  };

  const handleContact = (provider) => {
    window.location.href = `/messages?userId=${provider?.id}`;
  };

  const handleSaveProvider = (providerId) => {
    setSavedProviders((prev) =>
      prev?.includes(providerId)
        ? prev?.filter((id) => id !== providerId)
        : [...prev, providerId]
    );
  };

  const handleCompareToggle = (provider) => {
    setCompareList((prev) => {
      const exists = prev?.find((p) => p?.id === provider?.id);
      if (exists) {
        return prev?.filter((p) => p?.id !== provider?.id);
      } else if (prev?.length < 3) {
        return [...prev, provider];
      } else {
        alert('You can compare up to 3 providers at a time');
        return prev;
      }
    });
  };

  const handleClearFilters = () => {
    setFilters({
      category: 'all',
      location: 'all',
      budget: { min: '', max: '' },
      availability: 'all',
      verificationLevel: 'all',
      minRating: null
    });
    setSelectedTags([]);
    setSearchQuery('');
  };

  const sortedWorkers = [...workers]?.sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return (b?.rating || 0) - (a?.rating || 0);
      case 'rate-low':
        return (a?.hourlyRate || 0) - (b?.hourlyRate || 0);
      case 'rate-high':
        return (b?.hourlyRate || 0) - (a?.hourlyRate || 0);
      case 'experience':
        return (b?.completedJobs || 0) - (a?.completedJobs || 0);
      case 'reviews':
        return (b?.reviews || 0) - (a?.reviews || 0);
      default:
        return 0;
    }
  });

  const featuredWorkers = sortedWorkers?.filter((w) => w?.isVerified)?.slice(0, 3);

  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      <Header />
      
      <main className="pt-[60px] lg:pt-[60px]">
        <div className="max-w-[1440px] mx-auto px-[16px] lg:px-[24px] py-[24px] lg:py-[32px]">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-2">
                  Find Service Providers
                </h1>
                <p className="text-sm lg:text-base text-muted-foreground">
                  Discover qualified Filipino workers for your projects
                </p>
              </div>
              <div className="flex items-center gap-2">
                {compareList?.length > 0 && (
                  <Button
                    variant="outline"
                    size="default"
                    onClick={() => setShowComparison(!showComparison)}
                    iconName="GitCompare"
                    iconPosition="left"
                    className="border-[#FF6A00] text-[#FF6A00] hover:bg-[#FF6A00]/10"
                  >
                    Compare ({compareList?.length})
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="default"
                  onClick={() => window.location.href = '/post-job'}
                  iconName="PlusCircle"
                  iconPosition="left"
                  className="border-[#1E73BE] text-[#1E73BE] hover:bg-[#1E73BE]/10"
                >
                  Post Job
                </Button>
              </div>
            </div>
          </div>

          {/* Featured Providers */}
          {featuredWorkers?.length > 0 && (
            <div className="mb-6">
              <div className="bg-gradient-to-r from-[#1E73BE] to-[#2A85D8] rounded-xl p-6 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Star" size={24} className="text-[#FF6A00] fill-[#FF6A00]" />
                  <h2 className="text-xl font-heading font-bold text-white">
                    Featured Service Providers
                  </h2>
                </div>
                <p className="text-white/90 text-sm">
                  Top-rated professionals with verified credentials
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {featuredWorkers?.map((worker) => (
                  <ServiceProviderCard
                    key={worker?.id}
                    provider={worker}
                    onViewProfile={handleViewProfile}
                    onContact={handleContact}
                    viewMode="card"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Search and Tags */}
          <div className="mb-6 space-y-4">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              onSearch={handleSearch}
              suggestions={searchSuggestions}
            />
            <TagFilter
              tags={availableTags}
              selectedTags={selectedTags}
              onTagToggle={handleTagToggle}
              onTagsChange={setSelectedTags}
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Filter Sidebar */}
            <div className="lg:col-span-1">
              <FilterPanel
                filters={filters}
                onFilterChange={setFilters}
                onClearFilters={handleClearFilters}
              />
            </div>

            {/* Results Grid */}
            <div className="lg:col-span-3">
              {/* View Controls */}
              <div className="bg-white border border-gray-100 rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Icon name="Users" size={20} color="#FF6A00" />
                  <span className="text-sm font-medium text-foreground">
                    {sortedWorkers?.length || 0} Service Providers Found
                  </span>
                </div>
                
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <Select
                    options={[
                      { value: 'rating', label: 'Highest Rated' },
                      { value: 'rate-low', label: 'Lowest Rate' },
                      { value: 'rate-high', label: 'Highest Rate' },
                      { value: 'experience', label: 'Most Experience' },
                      { value: 'reviews', label: 'Most Reviews' }
                    ]}
                    value={sortBy}
                    onChange={setSortBy}
                    className="flex-1 sm:flex-initial sm:w-[180px]"
                  />
                  
                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('card')}
                      className={`p-2 rounded transition-colors ${
                        viewMode === 'card' ? 'bg-white text-[#FF6A00] shadow-sm' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon name="LayoutGrid" size={18} />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded transition-colors ${
                        viewMode === 'list' ? 'bg-white text-[#FF6A00] shadow-sm' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon name="List" size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Loading State */}
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6A00]"></div>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                  <Icon name="AlertCircle" size={48} color="#EF4444" className="mx-auto mb-3" />
                  <p className="text-red-600 font-medium mb-2">Error Loading Data</p>
                  <p className="text-sm text-red-500">{error}</p>
                </div>
              )}

              {/* Service Providers Grid */}
              {!loading && !error && (
                <div className={viewMode === 'card' ?'grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4' :'space-y-4'
                }>
                  {sortedWorkers?.length > 0 ? (
                    sortedWorkers?.map((worker) => (
                      <ServiceProviderCard
                        key={worker?.id}
                        provider={worker}
                        onViewProfile={handleViewProfile}
                        onContact={handleContact}
                        viewMode={viewMode}
                      />
                    ))
                  ) : (
                    <div className="col-span-full text-center py-12">
                      <Icon name="Search" size={48} color="#9CA3AF" className="mx-auto mb-3" />
                      <p className="text-muted-foreground">No service providers found matching your criteria</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Comparison Panel */}
          {showComparison && compareList?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-heading font-semibold text-foreground">
                  Comparing {compareList?.length} Providers
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCompareList([])}
                  iconName="X"
                  className="border-red-500 text-red-500 hover:bg-red-50"
                >
                  Clear All
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {compareList?.map((provider) => (
                  <div key={provider?.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={provider?.image}
                        alt={provider?.imageAlt || `${provider?.name} profile`}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm truncate">{provider?.name}</h4>
                        <p className="text-xs text-muted-foreground">{provider?.category}</p>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Rating:</span>
                        <span className="font-semibold">{provider?.rating} ⭐</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Rate:</span>
                        <span className="font-semibold">₱{provider?.hourlyRate}/hr</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Jobs:</span>
                        <span className="font-semibold">{provider?.completedJobs}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Reviews:</span>
                        <span className="font-semibold">{provider?.reviews}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ServiceProviders;